import { Reminder } from '@griply/types';
import { resolveScheduleStartDate } from '@griply/utils';
import React, { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoSizeTextArea } from 'shared/components/ui/auto-size-text-area';
import { Checkbox, getTaskCheckboxColor } from 'shared/components/ui/checkbox';
import { Collapse } from 'shared/components/ui/collapse';
import { NLPInput } from 'shared/components/ui/n-l-p-input';
import { RemindersMenu } from 'shared/components/ui/reminders-menu';
import { TaskMetaInteractionDeadlineDeadline } from 'shared/components/ui/task-meta-interaction-deadline-deadline';
import { TaskMetaInteractionDueDate } from 'shared/components/ui/task-meta-interaction-due-date';
import { TaskMetaInteractionDuration } from 'shared/components/ui/task-meta-interaction-duration';
import { TaskMetaInteractionLinkedItem } from 'shared/components/ui/task-meta-interaction-linked-item';
import { TaskMetaInteractionPriority } from 'shared/components/ui/task-meta-interaction-priority';
import { TaskMetaInteractionRepeat } from 'shared/components/ui/task-meta-interaction-repeat';
import { TaskMetaInteractionStartTime } from 'shared/components/ui/task-meta-interaction-start-time';
import { TaskMetaInteractionTags } from 'shared/components/ui/task-meta-interaction-tags';
import { useToday } from 'shared/contexts/today';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { DataType } from 'shared/types/data-type';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal, GoalType, NewGoal, NewGoalWithID } from 'shared/types/goal';
import { GoalLinkPickerSlotProps } from 'shared/types/goal-link-picker';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { NewEntityTypes } from 'shared/types/nlp';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { CreateTask, LinkOptions } from 'shared/types/task';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { focusOnEndOfEditableContent } from 'shared/utils/focus-on-end-of-element';
import { getIsMacOs } from 'shared/utils/get-is-mac-os';
import { isNumber } from 'shared/utils/is-number';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';

import * as Styled from './new-task-card.style';

const noop = () => {};

export type NewTaskCardProps = {
  LinkPicker?: React.FC<GoalLinkPickerSlotProps>;
  name: string;
  type?: TaskType;
  description?: string | null;
  placeholder?: string;
  initialDeadline?: Timestamp;
  deadline: Timestamp | null;
  timeslot: Timeslot | null;
  deadlineDeadline: Timestamp | null;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  schedules: HabitSchedule[] | null;
  goalId?: ID | null;
  lifeAreaId?: ID | null;
  tagIds: ID[] | null;
  goals: (Goal | NewGoalWithID)[];
  lifeAreas: LifeArea[];
  tags: (Tag | NewTagWithID)[];
  priority?: TaskPriorityOptions | null;
  reminderTime: number | null;
  reminders: Reminder[] | null;
  onChange: (task: Partial<CreateTask>) => void;
  onChangeName: (
    value: string,
    extraInfo: {
      wordIds: WordId[];
      ignoredWordIds: ID[];
    },
  ) => void;
  onSubmit: (
    value: string,
    extraInfo: {
      wordIds: WordId[];
      ignoredWordIds: ID[];
    },
  ) => void;
  onClick: () => void;
  onCreateGoal: (goal: NewGoal) => void;
  onCreateTag: (name: string) => void;
  onPremium: () => void;
  scheduleTargetAllowed: boolean;
  createHabitAllowed: boolean;
  createGoalAllowed: boolean;
  createProjectAllowed: boolean;
  nameInputRef?: React.RefObject<HTMLDivElement>;
  isSelected?: boolean;
  onRequestSubmit?: () => void;
  noMeta?: boolean;
  onBulkPaste?: (tasks: string[]) => void;
  isCheckable?: boolean;
  isChecked?: boolean;
  onToggleCheck?: () => void;
};

export const NewTaskCard = forwardRef<HTMLDivElement, NewTaskCardProps>(
  (
    {
      LinkPicker,
      name,
      type,
      description,
      placeholder,
      initialDeadline,
      deadline,
      timeslot,
      deadlineDeadline,
      dateFormat,
      weekStartsOn,
      timeFormat,
      schedules,
      goalId,
      lifeAreaId,
      tagIds,
      goals,
      lifeAreas,
      tags,
      priority,
      reminderTime,
      reminders,
      onChange,
      onChangeName,
      onSubmit,
      onRequestSubmit,
      onClick,
      onCreateGoal,
      onCreateTag,
      onPremium,
      createHabitAllowed,
      scheduleTargetAllowed,
      createGoalAllowed,
      createProjectAllowed,
      nameInputRef = null,
      isSelected,
      noMeta,
      onBulkPaste,
      isCheckable,
      isChecked,
      onToggleCheck,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const locale = useI18nLocale();
    const today = useToday();
    const inputRef = useForwardedRef(nameInputRef);
    const onContainer = () => onClick();
    const schedule = schedules?.[0];
    const _timeslot = schedule ? schedule.timeslot : timeslot;

    const onDescriptionKeyDown = (
      e: React.KeyboardEvent<HTMLTextAreaElement>,
    ) => {
      const isShortcut = getIsMacOs() ? e.metaKey : e.ctrlKey;

      if (isShortcut && e.key === 'Enter') {
        e.stopPropagation();
        // Trigger submit with current values
        if (name && onRequestSubmit) {
          onRequestSubmit();
        }
      }
    };

    const onChangeDescription = ({
      target,
    }: React.ChangeEvent<HTMLTextAreaElement>) =>
      onChange({ description: target.value });

    const handleMetaChange = (task: Partial<CreateTask>) => {
      onChange(task);

      requestAnimationFrame(() => {
        if (inputRef.current) {
          focusOnEndOfEditableContent(inputRef.current);
        }
      });
    };

    const onChangeRepeat = (changes: {
      type: TaskType;
      schedules: HabitSchedule[] | null;
    }) => {
      const nextSchedules = changes.schedules;
      const addedSchedule = nextSchedules?.[nextSchedules.length - 1];
      if (!nextSchedules || !addedSchedule || !deadline) {
        handleMetaChange(changes);
        return;
      }

      handleMetaChange({
        ...changes,
        endStrategy: null,
        schedules: nextSchedules.map((candidate) =>
          candidate === addedSchedule
            ? {
                ...candidate,
                startDate: resolveScheduleStartDate(deadline, today),
              }
            : candidate,
        ),
      });
    };

    const onChangeStartDateOrDeadline = (date?: Timestamp) =>
      handleMetaChange({
        endStrategy: schedule
          ? null
          : { deadline: date, completionCount: null },
        schedules: schedule
          ? [
              {
                ...schedule,
                startDate: date ?? schedule.startDate,
              },
            ]
          : null,
      });

    const onChangeStartTime = (startTime: number | null) => {
      const slot =
        isNumber(startTime) || isNumber(_timeslot?.duration)
          ? {
              startTime: isNumber(startTime) ? startTime : null,
              duration: isNumber(_timeslot?.duration)
                ? _timeslot!.duration
                : null,
            }
          : null;

      handleMetaChange({
        timeslot: schedule ? null : slot,
        schedules: schedule
          ? [
              {
                ...schedule,
                timeslot: slot,
              },
            ]
          : null,
      });
    };

    const onChangeDuration = (duration: number | null) => {
      const slot =
        isNumber(duration) || isNumber(_timeslot?.startTime)
          ? {
              startTime: isNumber(_timeslot?.startTime)
                ? _timeslot!.startTime
                : null,
              duration: isNumber(duration) ? duration : null,
            }
          : null;

      handleMetaChange({
        timeslot: schedule ? null : slot,
        schedules: schedule
          ? [
              {
                ...schedule,
                timeslot: slot,
              },
            ]
          : null,
      });
    };

    const onChangeDeadlineDeadline = (date?: Timestamp) =>
      handleMetaChange({ deadlineDeadline: date });

    const onChangeGoal = (link: { id: ID; type: LinkOptions } | null) =>
      handleMetaChange({
        lifeAreaId: link?.type === LinkOptions.LifeArea ? link.id : null,
        goalId: link?.type === LinkOptions.Goal ? link.id : null,
      });

    const onChangeTag = (tagIds: ID[]) => handleMetaChange({ tagIds });

    const onChangeReminder = (reminders: Reminder[]) =>
      handleMetaChange({ reminders });

    const onChangePriority = (priority: TaskPriorityOptions | null) =>
      handleMetaChange({ priority });

    const onCreateEntity = (entity: NewEntityTypes, name: string) => {
      if (entity === NewEntityTypes.Goal) {
        onCreateGoal({ name });
      }

      if (entity === NewEntityTypes.Project) {
        onCreateGoal({ name, type: GoalType.Project });
      }

      if (entity === NewEntityTypes.Tag) {
        onCreateTag(name);
      }

      requestAnimationFrame(() => {
        if (inputRef.current) {
          focusOnEndOfEditableContent(inputRef.current);
        }
      });
    };

    useEffect(() => {
      isSelected ? inputRef.current?.focus() : inputRef.current?.blur();
    }, [isSelected, inputRef]);

    return (
      <Styled.Container
        ref={ref}
        onClick={onContainer}
        data-component-type={DataType.NewTask}
      >
        <Styled.Header>
          <Styled.CheckboxContainer>
            <Checkbox
              color={getTaskCheckboxColor(undefined, priority)}
              checked={isChecked ?? false}
              readOnly={!isCheckable}
              isPlaceholder={!isCheckable}
              onChange={onToggleCheck}
            />
          </Styled.CheckboxContainer>

          <Styled.HeaderContent>
            <Styled.TitleContainer $isFaded={false}>
              <NLPInput
                ref={inputRef}
                value={name}
                placeholder={placeholder}
                goals={goals}
                lifeAreas={lifeAreas}
                tags={tags}
                language={locale}
                dateFormat={dateFormat}
                createTargetHabitAllowed={scheduleTargetAllowed}
                createHabitAllowed={createHabitAllowed}
                createGoalAllowed={createGoalAllowed}
                createProjectAllowed={createProjectAllowed}
                onChange={onChangeName}
                onSubmit={onSubmit}
                onPremium={onPremium}
                onBulkPaste={onBulkPaste ?? noop}
                onNewEntity={onCreateEntity}
              />
            </Styled.TitleContainer>
          </Styled.HeaderContent>
        </Styled.Header>

        {!noMeta && (
          <Collapse isOpen={isSelected}>
            {isSelected && (
              <Styled.CollapsedContainer>
                <Styled.Body>
                  <AutoSizeTextArea
                    value={description ?? ''}
                    placeholder={t(
                      'general.labels.task-detail.notes.placeholder',
                    )}
                    onChange={onChangeDescription}
                    inheritTypography
                    onKeyDown={onDescriptionKeyDown}
                  />
                </Styled.Body>

                <Styled.Footer>
                  <TaskMetaInteractionLinkedItem
                    LinkPicker={LinkPicker}
                    goals={goals}
                    lifeAreas={lifeAreas}
                    value={goalId ?? lifeAreaId}
                    onChange={onChangeGoal}
                    placeholder={t('task.linked-item.placeholder')}
                    onCreateGoal={onCreateGoal}
                    isCreate
                    hasIcon
                    highlight
                  />
                  <TaskMetaInteractionDueDate
                    dateFormat={dateFormat}
                    weekStartsOn={weekStartsOn}
                    onChange={onChangeStartDateOrDeadline}
                    placeholder={t('task.due-date.placeholder')}
                    initialDeadline={initialDeadline}
                    value={
                      (schedules ? schedules[0].startDate : deadline) ??
                      undefined
                    }
                    tomorrowLabelAllowed
                    hasIcon
                    highlight
                  />
                  <TaskMetaInteractionStartTime
                    value={_timeslot?.startTime}
                    timeFormat={timeFormat}
                    onChange={onChangeStartTime}
                    placeholder={t('task.timeslot.start-time.placeholder')}
                    hasIcon
                    highlight
                  />
                  <TaskMetaInteractionDuration
                    value={_timeslot?.duration}
                    onChange={onChangeDuration}
                    placeholder={t('task.timeslot.duration.placeholder')}
                    hasIcon
                    highlight
                  />
                  <TaskMetaInteractionDeadlineDeadline
                    dateFormat={dateFormat}
                    weekStartsOn={weekStartsOn}
                    onChange={onChangeDeadlineDeadline}
                    placeholder={t('task.deadline-deadline.placeholder')}
                    value={deadlineDeadline ?? undefined}
                    highlight
                  />
                  <TaskMetaInteractionPriority
                    value={priority}
                    onChange={onChangePriority}
                    highlight
                  />
                  <TaskMetaInteractionTags
                    tags={tags}
                    onChange={onChangeTag}
                    onCreate={onCreateTag}
                    value={tagIds}
                    highlight
                  />
                  <RemindersMenu
                    onChange={onChangeReminder}
                    reminders={reminders}
                    reminderTime={reminderTime}
                    hasTime={isNumber(_timeslot?.startTime)}
                    timeFormat={timeFormat}
                    highlight
                  />
                  <TaskMetaInteractionRepeat
                    value={schedules}
                    type={type ?? TaskType.Task}
                    weekStartsOn={weekStartsOn}
                    targetAllowed={scheduleTargetAllowed}
                    changeToHabitAllowed={createHabitAllowed}
                    onPremium={onPremium}
                    onChange={onChangeRepeat}
                  />
                </Styled.Footer>
              </Styled.CollapsedContainer>
            )}
          </Collapse>
        )}
      </Styled.Container>
    );
  },
);
